import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { KTSVG, toAbsoluteUrl } from '../../../../../_metronic/helpers'
import { useLanguageField } from '../../../../hooks/components/useLanguageFiled'
import { useNearBottom } from '../../../../hooks/utils/useNearBottom'

const defaultLanguages = [
  'English',
  'Spanish',
  'German',
  'French',
  'Italian',
  'Portuguese',
  'Simplified Chinese',
  'Traditional Chinese',
  'Japanese',
  'Korean',
]

const PAGE_SIZE = 10

type Props = {
  values: string[]
  onChange: (val: string[]) => void
  options?: string[]
  placeholder?: string
  minChars?: number
}

export function LanguageAutocompleteField({
  values,
  onChange,
  options,
  placeholder = 'Type language...',
  minChars = 0,
}: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const { collection } = useLanguageField(debounced)
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  const data = useMemo(() => {
    if (options) return options

    const fromApi = collection
      .map((item: any) => (typeof item === 'string' ? item : item?.name ?? ''))
      .filter(Boolean)

    return fromApi.length > 0 ? fromApi : defaultLanguages
  }, [options, collection]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [debounced])

  // debounce
  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced(query.trim())
    }, 200)

    return () => clearTimeout(id)
  }, [query])

  const filtered = useMemo(() => {
  const trimmed = debounced.trim()
  const base = data.filter((c) => !values.includes(c))
  if (trimmed.length < minChars) return base
  const q = trimmed.toLowerCase()
  return base.filter((c) => c.toLowerCase().includes(q))
  }, [debounced, minChars, data, values])

  const visible = useMemo(
    () => filtered.slice(0, visibleCount),
    [filtered, visibleCount]
  )
  
  const hasMore = filtered.length > visibleCount

  const highlightMatch = useCallback((text: string, search: string) => {
    if (!search) return text

    const safe = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(`(${safe})`, 'gi')
    const parts = text.split(regex)

    return (
      <>
        {parts.map((part, index) =>
          part.toLowerCase() === search.toLowerCase() ? (
            <span key={index} style={{ fontWeight: 700 }}>
              {part}
            </span>
          ) : (
            <span key={index} style={{ fontWeight: 300 }}>
              {part}
            </span>
          )
        )}
      </>
    )
  }, [])

  const handleAdd = (lang: string) => {
    if (!values.includes(lang)) {
      onChange([...values, lang])
    }
    setQuery('')
    setOpen(false)
  }

  const handleRemove = (lang: string) => {
    onChange(values.filter((v) => v !== lang))
  }

  const handleScroll = useNearBottom(() => {
    if (hasMore) setVisibleCount((c) => c + PAGE_SIZE)
  })

  return (
    <div className='nb-language-autocomplete'>
      <div className='nb-language-wrapper'>
        {values.map((lang) => (
          <div key={lang} className='nb-language-chip'>
            <div className='nb-language-chip-text'>{lang}</div>
            <div
              className='nb-language-chip-remove'
              onClick={() => handleRemove(lang)}
            >
              <KTSVG path={toAbsoluteUrl('/media/svg/nobilis/remove.svg')} />
            </div>
          </div>
        ))}

        <input
          className='nb-language-input'
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
        />
      </div>

      {open && visible.length > 0 && (
        <div className='nb-language-menu' onScroll={handleScroll}>
          {visible.map((label) => (
            <div
              key={label}
              className='nb-language-item'
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleAdd(label)}
            >
              {highlightMatch(label, debounced)}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}